import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig.js';
import { onAuthStateChanged } from 'firebase/auth';
import {
  FaArrowLeft,
  FaSearch,
  FaFilter,
  FaEye,
  FaCheck,
  FaTimes,
  FaReply,
  FaVideo,
  FaFile,
  FaImage,
  FaFilePdf,
  FaQuestion,
  FaCalculator,
  FaBook,
  FaClock,
  FaUser,
  FaCalendarAlt,
  FaTag,
  FaSave,
  FaSpinner,
  FaLink,
  FaGraduationCap,
  FaExclamationTriangle,
  FaInfoCircle
} from 'react-icons/fa';
import './MateriaisAprovacao.css';
import { normalizeText } from '../utils/text';

function MateriaisAprovacao() {
  const navigate = useNavigate();
  
  // Estados principais
  const [materiais, setMateriais] = useState([]);
  const [materiaisFiltrados, setMateriaisFiltrados] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Filtros
  const [filtroStatus, setFiltroStatus] = useState('pendente');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [termoBusca, setTermoBusca] = useState('');
  
  // Modal de visualização/resposta
  const [materialSelecionado, setMaterialSelecionado] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [resposta, setResposta] = useState('');
  const [videoResposta, setVideoResposta] = useState('');
  const [salvandoResposta, setSalvandoResposta] = useState(false);
  const [mensagem, setMensagem] = useState('');

  // Autenticação
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, firebaseUser => {
      setCurrentUser(firebaseUser);
      if (firebaseUser) {
        // O usuário já é hidratado no App; evitar sobrescrever contexto aqui.
      } else {
        navigate('/admin-login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Carregar materiais
  useEffect(() => {
    if (currentUser) {
      carregarMateriais();
    }
  }, [currentUser]);

  // Aplicar filtros
  useEffect(() => {
    aplicarFiltros();
  }, [materiais, filtroStatus, filtroTipo, termoBusca]);

  const carregarMateriais = async () => {
    setCarregando(true);
    try {
      const q = query(
        collection(db, 'student_materials'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      const materiaisData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt),
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : new Date(doc.data().updatedAt)
      }));
      
      setMateriais(materiaisData);
    } catch (error) {
      console.error('Erro ao carregar materiais:', error);
      setMensagem('Erro ao carregar materiais.');
    } finally {
      setCarregando(false);
    }
  };

  const aplicarFiltros = () => {
    let filtrados = materiais;

    // Filtro por status
    if (filtroStatus) {
      filtrados = filtrados.filter(material => material.status === filtroStatus);
    }

    // Filtro por tipo
    if (filtroTipo) {
      filtrados = filtrados.filter(material => material.tipo === filtroTipo);
    }

    // Filtro por termo de busca
    if (termoBusca) {
      const termo = normalizeText(termoBusca);
      filtrados = filtrados.filter(material =>
        normalizeText(material.titulo).includes(termo) ||
        normalizeText(material.descricao).includes(termo) ||
        normalizeText(material.authorName).includes(termo) ||
        normalizeText(material.conteudo).includes(termo) ||
        normalizeText(material.disciplina).includes(termo) ||
        normalizeText(material.curso).includes(termo)
      );
    }

    setMateriaisFiltrados(filtrados);
  };

  const abrirModal = (material) => {
    setMaterialSelecionado(material);
    setResposta(material.resposta || '');
    setVideoResposta(material.videoResposta || '');
    setModalAberto(true);
    setMensagem('');
  };

  const fecharModal = () => {
    setModalAberto(false);
    setMaterialSelecionado(null);
    setResposta('');
    setVideoResposta('');
    setMensagem('');
  };

  const alterarStatus = async (materialId, novoStatus) => {
    try {
      await updateDoc(doc(db, 'student_materials', materialId), {
        status: novoStatus,
        updatedAt: new Date(),
        reviewedBy: currentUser.email
      });

      // Atualizar localmente
      setMateriais(prev => prev.map(material =>
        material.id === materialId
          ? { ...material, status: novoStatus, reviewedBy: currentUser.email }
          : material
      ));

      setMensagem(`Material ${novoStatus === 'aprovado' ? 'aprovado' : 'rejeitado'} com sucesso!`);
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      setMensagem('Erro ao alterar status do material.');
    }
  };

  const salvarResposta = async () => {
    if (!materialSelecionado) return;

    setSalvandoResposta(true);
    try {
      await updateDoc(doc(db, 'student_materials', materialSelecionado.id), {
        resposta: resposta,
        videoResposta: videoResposta,
        respondidoEm: new Date(),
        respondidoPor: currentUser.email,
        status: 'aprovado', // Automaticamente aprova quando responde
        updatedAt: new Date()
      });

      // Atualizar localmente
      setMateriais(prev => prev.map(material =>
        material.id === materialSelecionado.id
          ? {
              ...material,
              resposta,
              videoResposta,
              respondidoEm: new Date(),
              respondidoPor: currentUser.email,
              status: 'aprovado'
            }
          : material
      ));

      setMensagem('Resposta salva e material aprovado com sucesso!');
      setTimeout(() => {
        fecharModal();
      }, 2000);
    } catch (error) {
      console.error('Erro ao salvar resposta:', error);
      setMensagem('Erro ao salvar resposta.');
    } finally {
      setSalvandoResposta(false);
    }
  };

  const getTipoIcon = (tipo, tipoSolicitacao) => {
    // Priorizar tipoSolicitacao se disponível
    if (tipoSolicitacao) {
      switch (tipoSolicitacao) {
        case 'duvida': return <FaQuestion />;
        case 'exercicio': return <FaCalculator />;
        case 'prova': return <FaGraduationCap />;
        case 'material': return <FaBook />;
        case 'outro': return <FaInfoCircle />;
        default: return <FaQuestion />;
      }
    }
    
    switch (tipo) {
      case 'duvida': return <FaQuestion />;
      case 'exercicio': return <FaCalculator />;
      case 'material': return <FaBook />;
      default: return <FaFile />;
    }
  };

  const getMaterialIcon = (tipoMaterial, materialType) => {
    if (tipoMaterial === 'video') return <FaVideo />;
    if (tipoMaterial === 'arquivo') {
      if (materialType === 'PDF') return <FaFilePdf />;
      if (['JPG', 'JPEG', 'PNG'].includes(materialType)) return <FaImage />;
      return <FaFile />;
    }
    return <FaFile />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'aprovado': return '#16a34a';
      case 'rejeitado': return '#dc2626';
      case 'pendente': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return null;
    
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const videoId = videoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      return videoId ? `https://img.youtube.com/vi/${videoId[1]}/hqdefault.jpg` : null;
    }
    
    if (videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.match(/(?:vimeo\.com\/)([0-9]+)/);
      return videoId ? `https://vumbnail.com/${videoId[1]}.jpg` : null;
    }
    
    return null;
  };

  const formatTipoSolicitacao = (tipoSolicitacao) => {
    const tipos = {
      'duvida': 'Dúvida sobre conteúdo',
      'exercicio': 'Resolução de exercício',
      'prova': 'Prova resolvida',
      'material': 'Material complementar',
      'outro': 'Outro'
    };
    return tipos[tipoSolicitacao] || tipoSolicitacao;
  };

  const formatUrgencia = (urgencia) => {
    const urgencias = {
      'normal': 'Normal (até 7 dias)',
      'urgente': 'Urgente (até 3 dias)',
      'muito_urgente': 'Muito urgente (até 24h)'
    };
    return urgencias[urgencia] || urgencia;
  };

  const formatFormatoResposta = (formatos) => {
    if (!formatos || !Array.isArray(formatos) || formatos.length === 0) return null;
    
    const labels = {
      'videoaula': 'Videoaula',
      'material_escrito': 'Material escrito',
      'resumo': 'Resumo',
      'exercicio_resolvido': 'Exercício resolvido'
    };
    
    return formatos.map(formato => labels[formato] || formato).join(', ');
  };

  return (
    <div className="materiais-aprovacao-container">
      {/* Header */}
      <header className="admin-header">
        <button className="back-button" onClick={() => navigate('/admin-panel')}>
          <FaArrowLeft /> Voltar ao Painel
        </button>
        <div className="header-title">
          <h1>Material Aguardando Aprovação</h1>
          <p>Gerencie materiais enviados pelos alunos</p>
        </div>
      </header>

      {/* Filtros */}
      <div className="filtros-container">
        <div className="filtros-linha">
          <div className="filtro-grupo">
            <label>Status:</label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="pendente">Pendente</option>
              <option value="aprovado">Aprovado</option>
              <option value="rejeitado">Rejeitado</option>
            </select>
          </div>

          <div className="filtro-grupo">
            <label>Tipo:</label>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="duvida">Dúvida</option>
              <option value="exercicio">Exercício</option>
              <option value="material">Material</option>
            </select>
          </div>

          <div className="filtro-busca">
            <FaSearch className="busca-icon" />
            <input
              type="text"
              placeholder="Buscar por título, autor ou conteúdo..."
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Lista de Materiais */}
      <div className="materiais-lista-container">
        {carregando ? (
          <div className="loading-container">
            <FaSpinner className="loading-spinner" />
            <p>Carregando materiais...</p>
          </div>
        ) : materiaisFiltrados.length === 0 ? (
          <div className="empty-state">
            <FaQuestion className="empty-icon" />
            <h3>Nenhum material encontrado</h3>
            <p>Não há materiais com os filtros selecionados.</p>
          </div>
        ) : (
          <div className="materiais-grid">
            {materiaisFiltrados.map(material => (
              <div key={material.id} className="material-card">
                <div className="material-header">
                  <div className="material-tipo">
                    {getTipoIcon(material.tipo, material.tipoSolicitacao)}
                    <span>{material.tipoSolicitacao ? formatTipoSolicitacao(material.tipoSolicitacao) : material.tipo}</span>
                  </div>
                  <div 
                    className="material-status" 
                    style={{ 
                      backgroundColor: getStatusColor(material.status),
                      color: 'white'
                    }}
                  >
                    {material.status === 'pendente' ? 'EM ANÁLISE' : material.status.toUpperCase()}
                  </div>
                </div>

                <div className="material-content">
                  <h3 className="material-titulo">{material.titulo}</h3>
                  <p className="material-descricao">{material.descricao}</p>
                  
                  <div className="material-meta">
                    <div className="meta-item">
                      <FaUser />
                      <span>{material.authorName}</span>
                    </div>
                    <div className="meta-item">
                      <FaCalendarAlt />
                      <span>{material.createdAt.toLocaleDateString('pt-BR')}</span>
                    </div>
                    {material.disciplina && (
                      <div className="meta-item">
                        <FaGraduationCap />
                        <span>{material.disciplina}</span>
                      </div>
                    )}
                    {material.categoria && (
                      <div className="meta-item">
                        <FaTag />
                        <span>{material.categoria}</span>
                      </div>
                    )}
                    {material.urgencia && material.urgencia !== 'normal' && (
                      <div className="meta-item">
                        <FaExclamationTriangle />
                        <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                          {formatUrgencia(material.urgencia)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Prévia do material */}
                  {material.tipoMaterial === 'video' && material.videoUrl && (
                    <div className="material-preview">
                      <img 
                        src={getVideoThumbnail(material.videoUrl)}
                        alt="Thumbnail do vídeo"
                        className="video-thumbnail-small"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}

                  {material.tipoMaterial === 'arquivo' && material.arquivoURL && (
                    <div className="material-preview">
                      <div className="arquivo-info">
                        {getMaterialIcon(material.tipoMaterial, material.materialType)}
                        <span>Arquivo anexado</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="material-actions">
                  <button
                    onClick={() => abrirModal(material)}
                    className="btn-visualizar"
                  >
                    <FaEye /> Ver Detalhes
                  </button>
                  
                  {material.status === 'pendente' && (
                    <>
                      <button
                        onClick={() => alterarStatus(material.id, 'aprovado')}
                        className="btn-aprovar"
                      >
                        <FaCheck /> Aprovar
                      </button>
                      <button
                        onClick={() => alterarStatus(material.id, 'rejeitado')}
                        className="btn-rejeitar"
                      >
                        <FaTimes /> Rejeitar
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Visualização/Resposta */}
      {modalAberto && materialSelecionado && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{materialSelecionado.titulo}</h2>
              <button onClick={fecharModal} className="modal-close">
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {/* Informações do Material */}
              <div className="material-info-section">
                <div className="info-grid">
                  <div className="info-item">
                    <label>Autor:</label>
                    <span>{materialSelecionado.authorName} ({materialSelecionado.authorEmail})</span>
                  </div>
                  <div className="info-item">
                    <label>Tipo:</label>
                    <span>{materialSelecionado.tipoSolicitacao ? formatTipoSolicitacao(materialSelecionado.tipoSolicitacao) : materialSelecionado.tipo}</span>
                  </div>
                  <div className="info-item">
                    <label>Data de Envio:</label>
                    <span>{materialSelecionado.createdAt.toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span style={{ color: getStatusColor(materialSelecionado.status) }}>
                      {materialSelecionado.status.toUpperCase()}
                    </span>
                  </div>
                  {materialSelecionado.disciplina && (
                    <div className="info-item">
                      <label>Disciplina:</label>
                      <span>{materialSelecionado.disciplina}</span>
                    </div>
                  )}
                  {materialSelecionado.curso && (
                    <div className="info-item">
                      <label>Curso:</label>
                      <span>{materialSelecionado.curso}</span>
                    </div>
                  )}
                  {materialSelecionado.semestre && (
                    <div className="info-item">
                      <label>Semestre:</label>
                      <span>{materialSelecionado.semestre}º Semestre</span>
                    </div>
                  )}
                  {materialSelecionado.urgencia && (
                    <div className="info-item">
                      <label>Urgência:</label>
                      <span style={{ 
                        color: materialSelecionado.urgencia === 'muito_urgente' ? '#dc2626' : 
                               materialSelecionado.urgencia === 'urgente' ? '#f59e0b' : '#6b7280',
                        fontWeight: materialSelecionado.urgencia !== 'normal' ? 'bold' : 'normal'
                      }}>
                        {formatUrgencia(materialSelecionado.urgencia)}
                      </span>
                    </div>
                  )}
                  {materialSelecionado.categoria && (
                    <div className="info-item">
                      <label>Categoria:</label>
                      <span>{materialSelecionado.categoria}</span>
                    </div>
                  )}
                  {materialSelecionado.subcategoria && (
                    <div className="info-item">
                      <label>Subcategoria:</label>
                      <span>{materialSelecionado.subcategoria}</span>
                    </div>
                  )}
                </div>

                {/* Preferências de formato de resposta */}
                {materialSelecionado.formatoResposta && materialSelecionado.formatoResposta.length > 0 && (
                  <div className="descricao-section">
                    <label>Formato de resposta preferido:</label>
                    <p style={{ color: '#209cd3', fontWeight: '500' }}>
                      {formatFormatoResposta(materialSelecionado.formatoResposta)}
                    </p>
                  </div>
                )}

                <div className="descricao-section">
                  <label>Descrição:</label>
                  <p>{materialSelecionado.descricao}</p>
                </div>

                {materialSelecionado.conteudo && (
                  <div className="conteudo-section">
                    <label>Conteúdo/Pergunta:</label>
                    <div className="conteudo-box">
                      {materialSelecionado.conteudo}
                    </div>
                  </div>
                )}

                {/* Link Relacionado */}
                {materialSelecionado.linkRelacionado && (
                  <div className="anexo-section">
                    <label>Link Relacionado:</label>
                    <a 
                      href={materialSelecionado.linkRelacionado}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="video-link"
                      style={{ display: 'inline-flex', marginTop: '0.5rem' }}
                    >
                      <FaLink /> {materialSelecionado.linkRelacionado}
                    </a>
                  </div>
                )}

                {/* Material Anexado */}
                {materialSelecionado.tipoMaterial === 'video' && materialSelecionado.videoUrl && (
                  <div className="anexo-section">
                    <label>Vídeo Anexado:</label>
                    <div className="video-preview">
                      <img 
                        src={getVideoThumbnail(materialSelecionado.videoUrl)}
                        alt="Thumbnail do vídeo"
                        className="video-thumbnail"
                        onClick={() => window.open(materialSelecionado.videoUrl, '_blank')}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <a 
                        href={materialSelecionado.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="video-link"
                      >
                        <FaVideo /> Assistir Vídeo
                      </a>
                    </div>
                  </div>
                )}

                {materialSelecionado.tipoMaterial === 'arquivo' && materialSelecionado.arquivoURL && (
                  <div className="anexo-section">
                    <label>Arquivo Anexado:</label>
                    <a 
                      href={materialSelecionado.arquivoURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="arquivo-link"
                    >
                      {getMaterialIcon(materialSelecionado.tipoMaterial, materialSelecionado.materialType)}
                      Ver Arquivo
                    </a>
                  </div>
                )}

                {/* Arquivo URL direto (se não for tipoMaterial arquivo mas existir arquivoURL) */}
                {materialSelecionado.tipoMaterial !== 'arquivo' && materialSelecionado.arquivoURL && (
                  <div className="anexo-section">
                    <label>Arquivo Anexado:</label>
                    <a 
                      href={materialSelecionado.arquivoURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="arquivo-link"
                    >
                      {getMaterialIcon('arquivo', materialSelecionado.materialType)}
                      Ver Arquivo
                    </a>
                  </div>
                )}

                {/* Material URL (campo legado) */}
                {materialSelecionado.materialUrl && materialSelecionado.materialUrl !== materialSelecionado.arquivoURL && (
                  <div className="anexo-section">
                    <label>Material URL:</label>
                    <a 
                      href={materialSelecionado.materialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="arquivo-link"
                    >
                      {getMaterialIcon('arquivo', materialSelecionado.materialType)}
                      Ver Material
                    </a>
                  </div>
                )}
              </div>

              {/* Seção de Resposta */}
              <div className="resposta-section">
                <h3>
                  <FaReply /> Resposta do Professor
                </h3>

                <div className="resposta-form">
                  <div className="form-group">
                    <label>Resposta em Texto:</label>
                    <textarea
                      value={resposta}
                      onChange={(e) => setResposta(e.target.value)}
                      placeholder="Digite sua resposta aqui..."
                      rows={6}
                    />
                  </div>

                  <div className="form-group">
                    <label>Vídeo Resposta (YouTube/Vimeo) - Opcional:</label>
                    <input
                      type="url"
                      value={videoResposta}
                      onChange={(e) => setVideoResposta(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=... ou https://vimeo.com/..."
                    />
                    {videoResposta && getVideoThumbnail(videoResposta) && (
                      <div className="video-preview-small">
                        <img 
                          src={getVideoThumbnail(videoResposta)}
                          alt="Prévia da resposta em vídeo"
                          className="video-thumbnail-small"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <p>Prévia da resposta em vídeo</p>
                      </div>
                    )}
                  </div>

                  {mensagem && (
                    <div className={`mensagem ${mensagem.includes('Erro') ? 'erro' : 'sucesso'}`}>
                      {mensagem}
                    </div>
                  )}

                  <div className="resposta-actions">
                    <button
                      onClick={salvarResposta}
                      disabled={salvandoResposta || !resposta.trim()}
                      className="btn-salvar-resposta"
                    >
                      {salvandoResposta ? (
                        <>
                          <FaSpinner className="spinner" /> Salvando...
                        </>
                      ) : (
                        <>
                          <FaSave /> Salvar Resposta e Aprovar
                        </>
                      )}
                    </button>

                    {materialSelecionado.status === 'pendente' && (
                      <div className="status-actions">
                        <button
                          onClick={() => {
                            alterarStatus(materialSelecionado.id, 'aprovado');
                            fecharModal();
                          }}
                          className="btn-aprovar"
                        >
                          <FaCheck /> Apenas Aprovar
                        </button>
                        <button
                          onClick={() => {
                            alterarStatus(materialSelecionado.id, 'rejeitado');
                            fecharModal();
                          }}
                          className="btn-rejeitar"
                        >
                          <FaTimes /> Rejeitar
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resposta Existente */}
                {materialSelecionado.resposta && (
                  <div className="resposta-existente">
                    <h4>Resposta Atual:</h4>
                    <div className="resposta-conteudo">
                      <p>{materialSelecionado.resposta}</p>
                      {materialSelecionado.videoResposta && (
                        <div className="video-resposta">
                          <img 
                            src={getVideoThumbnail(materialSelecionado.videoResposta)}
                            alt="Resposta em vídeo"
                            className="video-thumbnail-small"
                            onClick={() => window.open(materialSelecionado.videoResposta, '_blank')}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <a 
                            href={materialSelecionado.videoResposta}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaVideo /> Ver Resposta em Vídeo
                          </a>
                        </div>
                      )}
                      {materialSelecionado.respondidoPor && (
                        <p className="resposta-meta">
                          Respondido por: {materialSelecionado.respondidoPor} em {' '}
                          {materialSelecionado.respondidoEm?.toLocaleDateString('pt-BR')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MateriaisAprovacao;
